import { useEffect } from "react";
import { formatData, statoBadgeClass } from "./comuni";

/* Pezzi del tab Percorsi condivisi dai tre sotto-tab (Pianificati / Oggi / Storico, task 517).
   Erano dentro TabPercorsi.js: ora che le sezioni stanno in file separati vivono qui, così
   nessun file di sezione deve importare dal contenitore (che importa le sezioni). */

// Stati finali del workflow 122: le opzioni del filtro Stato dello Storico.
export const STATI_CONCLUSI = ["Percorso non eseguito", "Percorso Parziale", "Percorso Completato"];

// "14/09 08:00" da un timestamp ISO (run dei job bozze e mail), in ora locale.
export const formatDataOra = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Foto attese per ogni tratta: mostrate come "scattate/attese" (es. 0/3) nel dettaglio.
export const FOTO_ATTESE_PER_TRATTA = 3;

// Un percorso "Percorso non eseguito" non ha report/foto: azioni disattivate.
export const isNonEseguito = (stato) => (stato || "").toLowerCase() === "percorso non eseguito";

// Un percorso ancora aperto ("Da Fare" / "In Lavorazione") non ha un report PDF da scaricare.
export const isAperto = (stato) => ["da fare", "in lavorazione"].includes((stato || "").toLowerCase());

// ---- Percorsi riaperti dall'app (task 532) ----
// `p.riapertura` = { il (ISO), giorno ("YYYY-MM-DD", Roma), statoPrecedente ('completato'|'parziale') },
// presente solo sui percorsi riaperti dal caposquadra e non ancora richiusi (lo decide il backend).
const STATO_PRIMA = { completato: "Completato", parziale: "Parziale" };
const eraStato = (r) => STATO_PRIMA[String(r?.statoPrecedente || "").toLowerCase()];

/** "Riaperto dall'operatore il 20/10, 14:32 (era Completato)": tooltip del badge e dettaglio. */
export const descriviRiapertura = (r) =>
    `Riaperto dall'operatore il ${formatDataOra(r.il)}${eraStato(r) ? ` (era ${eraStato(r)})` : ""}`;

/** "Riaperto il 17/10/2026 (era Completato) e non più richiuso": riapertura dimenticata (Da chiudere). */
export const riaperturaNonRichiusa = (r) =>
    `Riaperto il ${formatData(r.giorno)}${eraStato(r) ? ` (era ${eraStato(r)})` : ""} e non più richiuso`;

/** Badge accanto allo stato: il caposquadra sta correggendo il percorso, non va chiuso. */
export const BadgeRiaperto = ({ r }) => (
    <span className="badge badge-riaperto" title={`${descriviRiapertura(r)}. Fuori dai conteggi finché non lo richiude.`}>
        Riaperto – in correzione
    </span>
);

/* Giro completato DOPO, con un giro di recupero (Storico): lo stato su Interacta resta
   quello della chiusura (spesso "Parziale"), il badge dice che le tratte mancanti sono
   state recuperate. `tratteRecuperate` arriva solo da GET /percorsi (Storico). */
export const BadgeRecupero = ({ n }) => (
    <span className="badge badge-recupero"
        title={`${n} tratt${n === 1 ? "a" : "e"} di questo giro ${n === 1 ? "è stata fatta" : "sono state fatte"} dopo, con un giro di recupero: in Reportistica contano nel Totale, non in "Senza recuperi".`}>
        Completato con recupero · {n}
    </span>
);

// Badge dello "Stato Foto" (Completato / Parziale / Non eseguito). Riusa le stesse classi colore.
export const fotoStatoBadgeClass = (stato) => {
    const s = (stato || "").toLowerCase();
    if (s === "non eseguito") return "badge-stato-noneseguite";
    if (s === "parziale") return "badge-stato-parziali";
    if (s === "completato") return "badge-stato-completate";
    return "badge-neutral";
};

/** I bottoni azione di un percorso: PDF (solo sui conclusi: un giro in corso non ha un
 *  report da scaricare), galleria foto, invia (non ancora attivo). */
export const AzioniPercorso = ({ p, reportLoadingId, onReport, onGallery, conReport = true }) => (
    <>
        {/* Scarica report PDF del percorso (info + vie + foto). Disattivato se "Non eseguito". */}
        {conReport && (
            <button
                className="icon-btn"
                title={isNonEseguito(p.stato) ? "Percorso non eseguito: nessun report" : "Scarica report PDF del percorso"}
                onClick={() => onReport(p)}
                disabled={reportLoadingId === p.id || isNonEseguito(p.stato)}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            </button>
        )}
        {/* Galleria foto del percorso. Disattivata se "Non eseguito". */}
        <button
            className="icon-btn primary"
            title={isNonEseguito(p.stato) ? "Percorso non eseguito: nessuna foto" : "Vedi le foto del percorso"}
            onClick={() => onGallery(p)}
            disabled={isNonEseguito(p.stato)}
        >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5L5 21" /></svg>
        </button>
        {/* Invia report: previsto, per ora disattivato. */}
        <button className="icon-btn" title="Invia report: non ancora disponibile" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
        </button>
    </>
);

/** Riga di tabella di un percorso (Oggi e Storico): click -> modale dettaglio. */
export const RigaPercorso = ({ p, onDetail, azioni }) => (
    <tr className="gp-row-click" onClick={() => onDetail(p)} title="Vedi dettaglio percorso">
        <td><span className="strong">{p.nome || "—"}</span><div className="codice" style={{ fontSize: 11 }}>{p.customId || "—"}</div></td>
        <td>{formatData(p.data)}</td>
        <td>{p.assegnatario?.nome || "—"}</td>
        <td><span className="muted">{p.nTratti} vie</span></td>
        <td>
            <span className={`badge ${statoBadgeClass(p.stato)}`}>{p.stato || "—"}</span>
            {p.riapertura && <div className="gp-riaperto-wrap"><BadgeRiaperto r={p.riapertura} /></div>}
            {p.tratteRecuperate > 0 && <div className="gp-riaperto-wrap"><BadgeRecupero n={p.tratteRecuperate} /></div>}
        </td>
        <td><span className={`badge ${fotoStatoBadgeClass(p.fotoStato)}`}>{p.fotoStato || "—"}</span></td>
        <td className="col-actions" onClick={(e) => e.stopPropagation()}>{azioni}</td>
    </tr>
);

/** Card di un percorso (vista ≤1024px): stessa per Oggi e Storico. */
export const CardPercorso = ({ p, onDetail, azioni }) => (
    <div className="gp-card">
        <div className="gp-card-clickable" onClick={() => onDetail(p)} title="Vedi dettaglio percorso">
            <div className="gp-card-head">
                <div className="gp-card-title">
                    {p.nome || "—"}
                    <span className="codice">{p.customId || "—"}</span>
                </div>
                <span className={`badge ${statoBadgeClass(p.stato)}`}>{p.stato || "—"}</span>
            </div>
            <div className="gp-card-body">
                {p.riapertura && <div className="gp-riaperto-wrap"><BadgeRiaperto r={p.riapertura} /></div>}
                {p.tratteRecuperate > 0 && <div className="gp-riaperto-wrap"><BadgeRecupero n={p.tratteRecuperate} /></div>}
                <div className="gp-card-row"><span className="gp-card-label">Data</span><span className="gp-card-value">{formatData(p.data)}</span></div>
                <div className="gp-card-row"><span className="gp-card-label">Assegnatario</span><span className="gp-card-value">{p.assegnatario?.nome || "—"}</span></div>
                <div className="gp-card-row"><span className="gp-card-label">Tratti</span><span className="gp-card-value muted">{p.nTratti} vie</span></div>
                <div className="gp-card-row"><span className="gp-card-label">Stato Foto</span><span className="gp-card-value"><span className={`badge ${fotoStatoBadgeClass(p.fotoStato)}`}>{p.fotoStato || "—"}</span></span></div>
            </div>
        </div>
        <div className="gp-card-actions">{azioni}</div>
    </div>
);

/** Intestazioni di colonna delle tabelle percorsi. `sortable` = {toggleSort, sortArrow}
 *  per lo Storico (ordinamento lato server); senza, intestazioni semplici (Oggi). */
const COLONNE = [
    ["nome", "Percorso"], ["data", "Data"], ["operatore", "Assegnatario"],
    ["nTratti", "Tratti"], ["stato", "Stato Percorso"], ["fotoStato", "Stato Foto"],
];
export const TestataPercorsi = ({ sortable }) => (
    <thead>
        <tr>
            {COLONNE.map(([key, label]) => (sortable
                ? <th key={key} onClick={() => sortable.toggleSort(key)} style={{ cursor: "pointer" }}>{label}{sortable.sortArrow(key)}</th>
                : <th key={key}>{label}</th>))}
            <th className="col-actions">Azioni</th>
        </tr>
    </thead>
);

/** Interruttore Tabella / Card (stato nel contenitore, comune ai tre sotto-tab). */
export const ViewToggle = ({ viewMode, onChange }) => (
    <div className="gp-view-bar">
        <div className="view-toggle-compact">
            <button className={`view-btn-compact ${viewMode === "table" ? "active" : ""}`} onClick={() => onChange("table")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9h18M3 15h18M9 3v18" /></svg>
                Tabella
            </button>
            <button className={`view-btn-compact ${viewMode === "cards" ? "active" : ""}`} onClick={() => onChange("cards")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                Card
            </button>
        </div>
    </div>
);

/** Bottone "Aggiorna" (ricarica da Interacta ignorando la cache). */
export const AggiornaBtn = ({ onClick }) => (
    <button className="btn btn-ghost gp-new-btn" onClick={onClick} title="Ricarica da Interacta (ignora la cache)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
        Aggiorna
    </button>
);

/** ESC chiude il tutto schermo: `attivo` = c'è una tabella in fullscreen, `chiudi` lo spegne. */
export const useEscChiude = (attivo, chiudi) => {
    useEffect(() => {
        if (!attivo) return;
        const onEsc = (e) => { if (e.key === "Escape") chiudi(); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [attivo, chiudi]);
};
